import React, { useMemo } from 'react'
import type { Role } from '@/types'
import type { UserListItem } from '@/types'
import { SectionLoader } from '@/components/common/SectionLoader'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { cn } from '@/lib/cn'

const cardClasses =
  'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800'

export interface UserMatrixProps {
  users: UserListItem[]
  roles: Role[]
  isLoading: boolean
  error: Error | null
  /** Re-runs the matrix query from the error state's retry button. */
  onRetry?: () => Promise<unknown> | void
}

export function UserMatrix({
  users,
  roles,
  isLoading,
  error,
  onRetry,
}: UserMatrixProps): React.ReactElement {
  const rows = useMemo(() => {
    const byRoleId = new Map<string, UserListItem[]>()
    for (const u of users) {
      const list = byRoleId.get(u.roleId) ?? []
      list.push(u)
      byRoleId.set(u.roleId, list)
    }
    return roles.map((role) => ({
      role,
      userList: byRoleId.get(role._id) ?? [],
    }))
  }, [users, roles])

  if (isLoading) {
    return (
      <div className={cardClasses}>
        <SectionLoader variant="table" cols={4} rows={6} label="Loading user permissions" />
      </div>
    )
  }

  if (error) {
    return (
      <QueryErrorState error={error} title="Couldn't load the user matrix" onRetry={onRetry} />
    )
  }

  if (roles.length === 0) {
    return (
      <div
        className={cn(
          'py-12 text-center text-sm text-gray-600 dark:text-gray-400',
          cardClasses
        )}
      >
        No roles yet. Create a role to see the user matrix.
      </div>
    )
  }

  return (
    <div className={cardClasses}>
      <div className="p-4 sm:p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead>
              <tr>
                <th className="w-[min(200px,30%)] border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                  Role
                </th>
                <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 text-center font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                  Count
                </th>
                <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                  Users
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ role, userList }) => (
                <tr
                  key={role._id}
                  className="border-b border-gray-100 transition-colors even:bg-gray-50/50 hover:bg-gray-50 dark:border-gray-700 dark:even:bg-gray-800/30 dark:hover:bg-gray-800/50"
                >
                  <td className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {role.name}
                    </div>
                    {role.description && (
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {role.description}
                      </div>
                    )}
                  </td>
                  <td className="border-b border-gray-100 px-4 py-3 text-center text-gray-700 dark:border-gray-700 dark:text-gray-300">
                    {userList.length}
                  </td>
                  <td className="border-b border-gray-100 px-4 py-3 text-gray-600 dark:border-gray-700 dark:text-gray-400">
                    {userList.length === 0 ? (
                      <span className="italic">—</span>
                    ) : (
                      <span className="inline-block max-w-md truncate" title={userList.map((u) => u.name).join(', ')}>
                        {userList.map((u) => u.name).join(', ')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

UserMatrix.displayName = 'UserMatrix'
